import { useTranslation } from "react-i18next";
import { useSession } from "contexts/AuthContext";
import { useFeatureFlags } from "contexts/FeatureFlagContext";
import { BetaBadge } from "components/BetaBadge";
import { NotIncludedInExportBadge } from "components/NotIncludedInExportBadge";
import { StyledTabItem, StyledTabsNavigation } from "components/StyledTabs";
import { KeywordsTabActions } from "components/KeywordsTab";
import { PRODUCT_DETAILS_TAB } from "./ProductNavigationBar.types";
import { ProductNavigationBarSalesDataActions } from "./ProductNavigationBarSalesDataActions";

interface ProductNavigationBarTabNavigationProps {
  activeTab: PRODUCT_DETAILS_TAB;
  hasGeneratedKeywords: boolean;
  isRegenerating: boolean;
  onTabClick: (tab: PRODUCT_DETAILS_TAB) => void;
  onPipelineInfoOpen: () => void;
  onRegenerateOpen: () => void;
  showReasoning: boolean;
  onShowReasoningChange: (value: boolean) => void;
  isSalesExplainerOpen: boolean;
  onSalesExplainerOpen: () => void;
}

export const ProductNavigationBarTabNavigation = ({
  activeTab,
  hasGeneratedKeywords,
  isRegenerating,
  onTabClick,
  onPipelineInfoOpen,
  onRegenerateOpen,
  showReasoning,
  onShowReasoningChange,
  isSalesExplainerOpen,
  onSalesExplainerOpen,
}: ProductNavigationBarTabNavigationProps) => {
  const { t } = useTranslation(["productDetails"]);
  const { user } = useSession();
  const { features } = useFeatureFlags();

  const tabs: StyledTabItem<PRODUCT_DETAILS_TAB>[] = [
    {
      value: PRODUCT_DETAILS_TAB.KEYWORDS,
      label: t("productDetails:tabs.keywords"),
      onClick: () => onTabClick(PRODUCT_DETAILS_TAB.KEYWORDS),
      action: hasGeneratedKeywords ? (
        <KeywordsTabActions
          showReasoning={showReasoning}
          onShowReasoningChange={onShowReasoningChange}
          onPipelineInfoOpen={onPipelineInfoOpen}
          isRegenerating={isRegenerating}
          onRefresh={onRegenerateOpen}
        />
      ) : undefined,
    },
    {
      value: PRODUCT_DETAILS_TAB.THEMA,
      label: t("productDetails:tabs.thema"),
      onClick: () => onTabClick(PRODUCT_DETAILS_TAB.THEMA),
      testId: "thema-tab",
    },
    {
      value: PRODUCT_DETAILS_TAB.BOOK_DESCRIPTION,
      label: t("productDetails:tabs.description"),
      onClick: () => onTabClick(PRODUCT_DETAILS_TAB.BOOK_DESCRIPTION),
      testId: "description-tab",
    },
    {
      value: PRODUCT_DETAILS_TAB.MARKETING,
      label: t("productDetails:tabs.marketing"),
      onClick: () => onTabClick(PRODUCT_DETAILS_TAB.MARKETING),
      testId: "marketing-tab",
      action: <NotIncludedInExportBadge />,
    },
    {
      value: PRODUCT_DETAILS_TAB.SALES_DATA,
      label: t("productDetails:tabs.salesData"),
      onClick: () => onTabClick(PRODUCT_DETAILS_TAB.SALES_DATA),
      testId: "sales-data-tab",
      action: (
        <div className="flex items-center gap-2">
          <NotIncludedInExportBadge />
          {!isSalesExplainerOpen && (
            <ProductNavigationBarSalesDataActions
              onExplainerOpen={onSalesExplainerOpen}
            />
          )}
        </div>
      ),
    },
  ];

  if (features.essenceIntegration) {
    tabs.unshift({
      value: PRODUCT_DETAILS_TAB.SUMMARY,
      label: t("productDetails:tabs.summary"),
      onClick: () => onTabClick(PRODUCT_DETAILS_TAB.SUMMARY),
      testId: "summary-tab",
      action: <NotIncludedInExportBadge />,
    });
  }

  if (features.showAPlusTab) {
    tabs.push({
      value: PRODUCT_DETAILS_TAB.A_PLUS_CONTENT,
      label: (
        <span className="inline-flex items-center">
          {t("productDetails:tabs.aPlusContent")}
          <BetaBadge />
        </span>
      ),
      onClick: () => onTabClick(PRODUCT_DETAILS_TAB.A_PLUS_CONTENT),
      testId: "a-plus-content-tab",
      action: <NotIncludedInExportBadge />,
    });
  }

  if (user.isSuperAdmin) {
    tabs.push({
      value: PRODUCT_DETAILS_TAB.RAW_DATA,
      label: t("productDetails:tabs.rawData"),
      onClick: () => onTabClick(PRODUCT_DETAILS_TAB.RAW_DATA),
      testId: "raw-data-tab",
    });
  }

  return <StyledTabsNavigation tabs={tabs} activeTab={activeTab} />;
};
